import { useState } from "react";
import { X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Input, Textarea } from "../Common/CardUIHelpers";
import { addWashPackage } from "../../../redux/slices/carWashSlice";

interface AddWashOverlayProps {
  onClose: () => void;
}

export const AddWashOverlay = ({ onClose }: AddWashOverlayProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    title: "",
    description: "",
    priceFrom: "",
    priceTo: "",
    imgSrc: "",
    services: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!form.title) newErrors.title = "Title is required";
    if (!form.description) newErrors.description = "Description is required";
    if (!form.priceFrom) newErrors.priceFrom = "Price from is required";
    if (!form.priceTo) newErrors.priceTo = "Price to is required";
    if (!form.imgSrc) newErrors.imgSrc = "Image URL is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    dispatch(
      addWashPackage({
        wash: {
          title: form.title,
          description: form.description
            .split("\n")
            .map((d) => d.trim())
            .filter(Boolean),
          priceFrom: form.priceFrom,
          priceTo: form.priceTo,
          imgSrc: form.imgSrc,
          services: form.services
            ? form.services
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        },
      }) as any
    );

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-xl max-h-[90vh] bg-popupLight dark:bg-popupDark rounded-2xl shadow-lg p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-bold mb-4 text-center">
          {t("Add Wash Package")}
        </h2>

        <div className="overflow-y-auto custom-scroll max-h-[75vh] pr-2">
          <div className="space-y-4">
            <Input
              label="Title"
              value={form.title}
              error={errors.title}
              onChange={(v: string) => handleChange("title", v)}
            />

            <Textarea
              label="Description (one per line)"
              value={form.description}
              error={errors.description}
              onChange={(v: string) => handleChange("description", v)}
            />

            <Input
              label="Price From"
              type="number"
              value={form.priceFrom}
              error={errors.priceFrom}
              onChange={(v: string) => handleChange("priceFrom", v)}
            />

            <Input
              label="Price To"
              type="number"
              value={form.priceTo}
              error={errors.priceTo}
              onChange={(v: string) => handleChange("priceTo", v)}
            />

            <Input
              label="Image URL"
              value={form.imgSrc}
              error={errors.imgSrc}
              onChange={(v: string) => handleChange("imgSrc", v)}
            />

            <Input
              label="Services (comma separated)"
              value={form.services}
              onChange={(v: string) => handleChange("services", v)}
            />
          </div>

          <button
            onClick={handleSubmit}
            className="mt-6 w-full flex items-center justify-center gap-2
              bg-secondaryText dark:bg-secondaryTextDark
              text-primaryText py-2 rounded-md
              hover:opacity-90 transition"
          >
            <Check size={18} />
            {t("Save Wash")}
          </button>
        </div>
      </div>
    </div>
  );
};
